import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { ClientService } from "@services";
import utils from "@utils";
import { ClientCard } from "./components";
import { SearchField, ScrollDetect, Tooltip, ConfirmationModal } from "../../../components";

import "./ClientList.scss";

const ClientList = ({ router, user }) => {
	const {
		location: { query },
	} = router;
	const { lang } = global;
	const displayCount = 25;
	const initialFilters = {
		includeInactive: query.includeInactive === "true",
		name: query.name || "",
	};

	const [view, setView] = useState("list");

	const [clients, setClients] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);

	const [filters, setFilters] = useState({ ...initialFilters });

	const [totals, setTotals] = useState({
		clients: 0,
		active: 0,
		upcoming: 0,
		completed: 0,
	});

	const _confirmationModal = useRef(null);

	const calculateTotals = useCallback(() => {
		let active = 0;
		let upcoming = 0;
		let completed = 0;

		clients.forEach(client => {
			active += client.ActiveJobCount;
			upcoming += client.OpenJobCount;
			completed += client.ClosedJobCount;
		});

		setTotals({
			active,
			upcoming,
			completed,
			clients: clients.length,
		});
	});

	/** Retrieves a paginated list of clients */
	const getClients = useCallback(() => {
		// Remove empty query parameters for the API request
		const queryParams = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);

		ClientService.getAll({
			...queryParams,
			pageNumber,
			displayCount,
			minimalData: true,
		})
			.then(({ data }) => {
				const newClients = data.ItemList ?? [];
				setClients(pageNumber > 1 ? [...clients, ...newClients] : newClients);
				setPageNumber(pageNumber + 1);
				setHasMore(newClients.length >= displayCount);
			})
			.catch(({ response: { data } }) => {
				setClients([]);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading clients"),
					title: "Error",
				});
			});
	});

	useEffect(() => {
		if (visible) {
			getClients();
			setVisible(false);
		}
	}, [visible]);

	// Anytime clients is updated, recalculate totals
	useEffect(calculateTotals, [clients]);

	/* Whenever a filter property is changed, retrieve new data from the API by clearing old data and making the ScrollDetect visible */
	/* This automatically calls getProjects */
	useEffect(() => {
		const params = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);
		router.push({ pathname: "/clients", query: params });
		setPageNumber(1);
		setClients([]);
		setHasMore(true);
	}, [filters.name, filters.includeInactive]);

	/**
	 * Deletes a client record
	 * @param {Number} id The ID number of the client to delete
	 */
	const deleteClient = id => {
		// Delete staff on server and then locally
		ClientService.delete(id)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Client successfully deleted"),
					title: lang.t("Success"),
				});

				setClients(clients.filter(client => client.Id !== id));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error deleting client"),
					title: "Error",
				});
			});
	};

	const openDeleteClientConfirmation = id => () => {
		_confirmationModal.current.openModal({
			title: lang.t("Delete Client"),
			message: `${lang.t("Are you sure you want to delete this client_")}
						${lang.t("All projects and jobs related to this client will be deleted_")}
						${lang.t("This action cannot be undone_")}`,
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Client")}`,
			onConfirm: () => deleteClient(id),
		});
	};

	/**
	 * Applies the most recent filter changes
	 * @param {Event} event The HTML event handled by this method
	 */
	const onFilter = ({ target: { value, type, name, checked } }) => {
		let newValue;
		if (type === "checkbox") newValue = checked ? "true" : "false";
		else newValue = value ?? "";

		// Only update the state if the incoming value is different than the current value
		if (newValue !== filters[name]) {
			const newFilters = { ...filters, [name]: newValue };
			setFilters(newFilters);
		}
	};

	/** Searches for clients using their name as a filter property */
	const searchClients = activeSearch => {
		onFilter({ target: { name: "name", value: activeSearch } });
	};

	/**
	 * Navigates the user to the appropriate client view for their role
	 * @param {Number} id The ID number for a client
	 */
	const onClickCard = id => {
		if (user.isCoordinator()) {
			router.push(`/clients/view/${id}`);
		} else {
			router.push(`/clients/form/${id}`);
		}
	};

	/**
	 * Navigates the user to the appropriate client view for their role
	 * @param {Number} Id The ID number for a client
	 */
	const onClickRow =
		Id =>
		({
			target: {
				dataset: { preventNavigation },
			},
		}) => {
			if (!preventNavigation) {
				if (user.isCoordinator()) {
					router.push(`/clients/view/${Id}`);
				} else {
					router.push(`/clients/form/${Id}`);
				}
			}
		};

	return (
		<div
			id="client-list-container"
			className="container"
		>
			<div
				id="client-list-title"
				className="row"
			>
				<div className="col-xs-7 col-sm-9">
					<h1>{lang.t("Clients")}</h1>
				</div>
				<div className="col-xs-5 col-sm-3">
					<button
						type="button"
						onClick={() => router.push("/clients/add")}
						className="btn btn-primary btn-medium pull-right"
					>
						{`+ ${lang.t("Add Client")}`}
					</button>
				</div>
			</div>
			<div
				id="client-list-submenu"
				className="row"
			>
				<div className="col-xs-8 col-sm-4">
					<SearchField
						inputGroupClass="form-group"
						placeholder={lang.t("Search Clients")}
						onSearch={searchClients}
						clearable
						defaultValue={filters.name}
					/>
					<div className="form-group">
						<div className="checkbox">
							<label htmlFor="include-inactive-check">
								<input
									id="include-inactive-check"
									type="checkbox"
									name="includeInactive"
									checked={filters.includeInactive === "true"}
									onChange={onFilter}
								/>
								{lang.t("Include Inactive")}
							</label>
						</div>
					</div>
				</div>
				<div className="col-xs-4 pull-right text-right hidden">
					<div className="btn-group">
						<button
							type="button"
							id="client-view-as-list"
							aria-label="view as list"
							onClick={() => setView("list")}
							className={`btn btn-${view === "list" ? "primary" : "default"} font-size-12 btn-white-gray`}
						>
							<i className="rkf rkf-list" />
						</button>
						<button
							type="button"
							id="client-view-as-grid"
							aria-label="view as grid"
							onClick={() => setView("grid")}
							className={`btn btn-${view === "grid" ? "primary" : "default"} font-size-12 btn-white-gray`}
						>
							<i className="rkf rkf-th" />
						</button>
					</div>
				</div>
			</div>
			<If condition={view === "list"}>
				<div
					id="client-list-table-header"
					className="row hidden-xs hidden-sm"
				>
					<div className="col-md-1">{lang.t("ID")}</div>
					<div className="col-md-2 text-center">{lang.t("RK Account")}</div>
					<div
						className="col-md-6"
						title={totals.clients}
					>
						{lang.t("Name")}
					</div>
					<div className="col-md" />
				</div>
			</If>
			<If condition={view === "list"}>
				<div
					id="client-list-table-body"
					className="container"
				>
					<For
						each="client"
						of={clients}
						index="index"
					>
						<div
							role="row"
							tabIndex="0"
							className="row"
							onClick={onClickRow(client.Id)}
							onKeyPress={({ key }) => {
								if (key === "Enter") onClickRow(client.Id);
							}}
							key={`client_item_${client.Id}`}
						>
							<div className="col-xs-4 col-md-1 col-xs-push-8 col-md-push-0 client-id">
								<span className="visible-xs-inline-block visible-sm-inline-block">
									{`${lang.t("ID")}:`}
									&nbsp;
								</span>
								{client.Id}
							</div>
							<div className="col-xs-4 col-md-2 col-xs-push-2 col-md-push-0 client-account text-center">
								<span className="visible-xs-inline-block visible-sm-inline-block">
									{`${lang.t("RK Account")}:`}
									&nbsp;
								</span>
								<If condition={client.RKAccount}>
									<button
										type="button"
										className="btn btn-link"
									>
										<i className="rkf rkf-check-mark" />
									</button>
								</If>
							</div>
							<div className="col-xs-8 col-md-6 col-xs-pull-4 col-md-pull-0">
								<strong className="visible-xs-inline visible-sm-inline">
									{client.Name}
								</strong>
								<span className="hidden-xs hidden-sm">{client.Name}</span>
								{/* To be restored once endpoint is updated to return values.
									<If condition={!user.IsStaffAdmin
											&& client.ClientCoordinatorIds.includes(user.Id)
											&& client.ClientCoordinatorIds.length === 1}
								>
									<i className="glyphicon glyphicon-eye-close text-muted space-left" title={lang.t('Non-agency level client')} />
								</If> */}
							</div>
							<div className="col-xs-0 col-md-0 rkpay-container" />
							<div
								className="col-xs-9 col-md-3 client-btn-container"
								data-prevent-navigation
							>
								<If condition={!user.isCoordinator()}>
									<Link
										id="go-to-projects"
										to={`/projects/list?clientId=${client.Id}`}
										className="btn btn-default btn-margin-4"
										data-prevent-navigation
									>
										{lang.t("Projects")}
									</Link>
								</If>
								<Tooltip content={lang.t("Reports")}>
									<Link
										aria-label="go to report"
										id="go-to-report"
										to={`/reports?reportType=client_overview&property=clientId&id=${client.Id}`}
										className="btn btn-default btn-icon-only btn-margin-4"
										data-prevent-navigation
									>
										<i
											className="rkf rkf-analytics"
											data-prevent-navigation
										/>
									</Link>
								</Tooltip>
								<Tooltip content={lang.t("Delete")}>
									<button
										type="button"
										id={`delete-client-${client.Id}`}
										aria-label="delete-client"
										className="btn btn-primary btn-icon-only btn-margin-4"
										onClick={openDeleteClientConfirmation(client.Id)}
										data-prevent-navigation
									>
										<i
											className="rkf rkf-trash"
											data-prevent-navigation
										/>
									</button>
								</Tooltip>
							</div>
						</div>
					</For>
					<Choose>
						<When condition={!hasMore && clients.length === 0 && filters.name === ""}>
							<div className="row text-center">
								<div className="col-sm-12">
									{lang.t("Your clients are listed here_")}
								</div>
								<div className="col-sm-12">
									{lang.t("You have not connected with any clients yet_")}
								</div>
							</div>
						</When>
						<When condition={!hasMore && clients.length === 0}>
							<div className="row text-center">
								<div className="col-sm-12">{lang.t("No clients found")}</div>
							</div>
						</When>
					</Choose>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={() => setVisible(true)}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
			</If>
			<If condition={view === "grid"}>
				<div
					id="card-grid"
					className="row"
				>
					<For
						each="card"
						of={clients}
					>
						<div
							className="col-sm-4"
							key={`card_${card.Id}`}
						>
							<div className="card-container">
								<ClientCard
									data={card}
									onClickCard={onClickCard}
								/>
							</div>
						</div>
					</For>
					<Choose>
						<When condition={!hasMore && clients.length === 0 && filters.name === ""}>
							<div className="empty-card">
								<span className="header">
									{lang.t("Your clients are listed here_")}
								</span>
								<span className="subheader">
									{lang.t("You have not connected with any clients yet_")}
								</span>
							</div>
						</When>
						<When condition={!hasMore && clients.length === 0}>
							<div className="empty-card">
								<span>{lang.t("No clients found")}</span>
							</div>
						</When>
					</Choose>
				</div>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</If>
			<ConfirmationModal ref={_confirmationModal} />
		</div>
	);
};

ClientList.propTypes = {
	user: PropTypes.object,
	location: PropTypes.object,
	router: PropTypes.object.isRequired,
};

ClientList.defaultProps = {
	user: {},
	location: {},
};

export default ClientList;
